import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "users_table_column_visibility";

/**
 * Custom hook for managing column visibility with localStorage persistence
 * @param {Array} columns - Array of column definitions
 * @returns {Object} Column visibility state and handlers
 */
export const useColumnVisibility = (columns) => {
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
  const [columnSearchQuery, setColumnSearchQuery] = useState("");

  // Initialize column visibility from localStorage or set defaults
  useEffect(() => {
    if (!columns || columns.length === 0) return;

    const savedVisibility = localStorage.getItem(STORAGE_KEY);
    if (savedVisibility) {
      try {
        const parsed = JSON.parse(savedVisibility);
        setColumnVisibility(parsed);
        return;
      } catch (error) {
        console.error("Error parsing saved column visibility:", error);
      }
    }

    // Set all columns visible by default
    const defaultVisibility = {};
    columns.forEach((col) => {
      defaultVisibility[col.accessorKey] = true;
    });
    setColumnVisibility(defaultVisibility);
  }, [columns]);

  // Persist column visibility to localStorage
  useEffect(() => {
    if (columnVisibility && Object.keys(columnVisibility).length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(columnVisibility));
    }
  }, [columnVisibility]);

  // Menu handlers
  const handleColumnMenuOpen = useCallback((event) => {
    setColumnMenuAnchor(event.currentTarget);
  }, []);

  const handleColumnMenuClose = useCallback(() => {
    setColumnMenuAnchor(null);
    setColumnSearchQuery("");
  }, []);

  // Toggle column visibility
  const handleToggleColumn = useCallback((columnKey) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  }, []);

  // Handler for MaterialReactTable's column visibility change
  const handleColumnVisibilityChange = useCallback((updater) => {
    setColumnVisibility((old) => {
      const newState = typeof updater === "function" ? updater(old) : updater;
      return newState;
    });
  }, []);

  return {
    columnVisibility,
    setColumnVisibility: handleColumnVisibilityChange,
    columnMenuAnchor,
    columnSearchQuery,
    setColumnSearchQuery,
    handleColumnMenuOpen,
    handleColumnMenuClose,
    handleToggleColumn,
  };
};

